import { IntegrationChannelActionsMenu } from '@modules/integrations/components/IntegrationChannelActionsMenu';
import { EditIntegrationChannelCTA } from '@modules/integrationsV2/components/EditIntegrationChannelCTA';
import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  getAllIntegrationChannelsMetadataDetails,
  getIntegrationDataLabel,
} from '@modules/integrationsV2/utils';
import { IntegrationInstructionsRounded } from '@mui/icons-material';
import { Box, List, ListItem, Paper, Stack, Typography } from '@mui/material';
import { IntegrationViewFragment } from 'generated/graphql';

interface ConnectedIntegrationChannelListProps {
  integrations: IntegrationViewFragment[];
  integrationSource: IntegrationSource;
}

export const ConnectedIntegrationChannelList = ({
  integrations,
  integrationSource,
}: ConnectedIntegrationChannelListProps) => {
  const sourceIntegrations = integrations.filter(
    (integration) =>
      integration.isActive &&
      integration.channel.type === integrationSource.channel.type &&
      integration.integrationType === integrationSource.integrationType,
  );

  const integrationChannels =
    getAllIntegrationChannelsMetadataDetails(sourceIntegrations);

  const renderContent = () => {
    if (!integrationChannels.length) {
      const integrationDataLabel = getIntegrationDataLabel(
        integrationSource.channel.type,
      );

      return (
        <Typography
          variant="body2"
          textAlign="center"
          mt={2}
          color="text.secondary"
          pb={4}
        >
          No {integrationDataLabel} added. Click on the{' '}
          <Box fontWeight="bold" component="span">
            + Add New
          </Box>{' '}
          button above to add.
        </Typography>
      );
    }

    return (
      <List
        dense
        sx={{
          pl: 1.5,
        }}
      >
        {integrationChannels.map((integrationChannel) => {
          return (
            <ListItem dense key={integrationChannel.channelID}>
              <Stack width="100%">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    minWidth={0}
                  >
                    <Box
                      width="22px"
                      component="img"
                      alignSelf="center"
                      src={integrationSource.channel.logo}
                      alt={integrationSource.channel.displayName}
                    />
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      overflow="hidden"
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {integrationChannel.channelName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({integrationChannel.workspaceName})
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack pl={2} direction="row" alignItems="center" spacing={1}>
                    <EditIntegrationChannelCTA
                      channelID={integrationChannel.channelID}
                      channelName={integrationChannel.channelName}
                      integrationID={integrationChannel.integrationID}
                      integrationChannel={integrationSource.channel}
                      integrationType={integrationSource.integrationType}
                      channelDescriptions={
                        integrationChannel.allChannelDescriptions
                      }
                    />
                    <IntegrationChannelActionsMenu
                      channelID={integrationChannel.channelID}
                      channelName={integrationChannel.channelName}
                      integrationID={integrationChannel.integrationID}
                      integrationChannel={integrationSource.channel}
                      integrationType={integrationSource.integrationType}
                    />
                  </Stack>
                </Stack>
                {integrationChannel.channelDescription && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    width="90%"
                    pl={4.2}
                  >
                    Description: {integrationChannel.channelDescription}
                  </Typography>
                )}
              </Stack>
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        pb: 0.5,
      }}
    >
      <Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IntegrationInstructionsRounded
            fontSize="small"
            sx={{
              color: 'text.secondary',
            }}
          />
          <Typography>Integrations ({integrationChannels.length})</Typography>
        </Stack>
        {renderContent()}
      </Stack>
    </Paper>
  );
};
